import React, { useEffect, useRef, useState } from 'react';

const ParticleGrid = ({ className = '', mouseTimeoutSeconds = 2 }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const particlesRef = useRef([]);
  const isMouseInBounds = useRef(false);
  const wavesRef = useRef([]);
  const mouseTimeoutRef = useRef(null);
  const lastMousePosRef = useRef({ x: -1000, y: -1000 });
  const messageParticlesRef = useRef(new Set());
  const messageRevealedRef = useRef(false);
  const messageTimeoutRef = useRef(null);
  const currentMessageIndexRef = useRef(0);
  const messagePositionRef = useRef({ x: 0.5, y: 0.5 }); // Normalized position (0-1)
  const activeTouchesRef = useRef(new Map()); // Track multiple touches
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const isTapRef = useRef(false);

  // Hardcoded list of secret messages
  const messages = [
    ";)",
    "<3",
    "c:"
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // Get the parent container (the one with id="top-section")
    const parentContainer = canvas.closest('#top-section') || canvas.parentElement;
    
    // Hardcoded color palette
    const colors = [
      '#e4e2d5', // Near black
      '#d3d2c6', // Charcoal
      '#b1afa6', // Dark grey
      '#ebe9dc', // Steel grey
      '#ebe9dc', // Medium dark grey
      '#7a7a7a', // Grey
      '#8c8c8c', // Medium grey
      '#a3a3a3', // Light grey
      '#b8b8b8', // Silver
      '#d1d1d1',  // Light silver
      '#d24825',
      '#3685cf',
      '#9b8fbe',
      '#ca8a35'
    ];

    class Wave {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        this.radius = 0;
        this.maxRadius = 1000;
        this.speed = 15;
        this.force = 4;
        this.opacity = 0.5;
        this.active = true;
      }

      update() {
        if (!this.active) return;
        
        this.radius += this.speed;
        this.opacity = 1 - (this.radius / this.maxRadius);
        
        if (this.radius > this.maxRadius) {
          this.active = false;
        }
      }

      affectParticle(particle) {
        if (!this.active) return;
        
        const dx = particle.x - this.x;
        const dy = particle.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Check if particle is near the wave's edge
        const waveThickness = 40;
        if (distance < this.radius + waveThickness && distance > this.radius - waveThickness) {
          const force = this.force * this.opacity;
          const angle = Math.atan2(dy, dx);
          particle.vx += Math.cos(angle) * force;
          particle.vy += Math.sin(angle) * force;
        }
      }

      draw() {
        // Wave is invisible - effect shown only through particles
      }
    }

    class Particle {
      constructor(x, y, isMessageParticle = false) {
        this.originX = x;
        this.originY = y;
        this.x = x;
        this.y = y;
        this.vx = 0;
        this.vy = 0;
        this.radius = 3;
        this.friction = 0.7;
        this.springForce = 0.2;
        this.mouseRadius = 200;
        this.pushForce = 2;
        this.isVisible = false;
        this.visibilityThreshold = 2; // Distance from origin to trigger visibility
        this.color = colors[Math.floor(Math.random() * colors.length)]; // Random color from palette
        this.isMessageParticle = isMessageParticle;
        this.messageRevealRadius = 150; // Radius for revealing message particles
        this.hasBeenRevealed = false;
        this.fadeOpacity = 2;
        this.isFading = false;
      }

      update() {
        // Check interaction with mouse/touch points
        let hasInteraction = false;
        
        // Check mouse interaction
        if (isMouseInBounds.current) {
          const dx = this.x - mouseRef.current.x;
          const dy = this.y - mouseRef.current.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < this.mouseRadius) {
            hasInteraction = true;
            const force = (1 - distance / this.mouseRadius) * this.pushForce;
            const angle = Math.atan2(dy, dx);
            this.vx -= Math.cos(angle) * force;
            this.vy -= Math.sin(angle) * force;
          }
          
          // Special visibility for message particles
          if (this.isMessageParticle && !this.isFading) {
            const wasVisible = this.isVisible;
            this.isVisible = distance < this.messageRevealRadius;
            
            // Track if message was just revealed
            if (!wasVisible && this.isVisible && !this.hasBeenRevealed) {
              this.hasBeenRevealed = true;
              if (!messageRevealedRef.current) {
                messageRevealedRef.current = true;
                // Start timer to fade and change message
                if (messageTimeoutRef.current) {
                  clearTimeout(messageTimeoutRef.current);
                }
                messageTimeoutRef.current = setTimeout(() => {
                  // Start fading all message particles
                  messageParticlesRef.current.forEach(particle => {
                    particle.isFading = true;
                  });
                }, 3000); // Show for 1 second before fading
              }
            }
          }
        }
        
        // Check touch interactions
        activeTouchesRef.current.forEach((touch) => {
          const dx = this.x - touch.x;
          const dy = this.y - touch.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < this.mouseRadius) {
            hasInteraction = true;
            const force = (1 - distance / this.mouseRadius) * this.pushForce;
            const angle = Math.atan2(dy, dx);
            this.vx -= Math.cos(angle) * force;
            this.vy -= Math.sin(angle) * force;
          }
          
          // Special visibility for message particles (touch)
          if (this.isMessageParticle && !this.isFading && distance < this.messageRevealRadius) {
            const wasVisible = this.isVisible;
            this.isVisible = true;
            
            if (!wasVisible && !this.hasBeenRevealed) {
              this.hasBeenRevealed = true;
              if (!messageRevealedRef.current) {
                messageRevealedRef.current = true;
                if (messageTimeoutRef.current) {
                  clearTimeout(messageTimeoutRef.current);
                }
                messageTimeoutRef.current = setTimeout(() => {
                  messageParticlesRef.current.forEach(particle => {
                    particle.isFading = true;
                  });
                }, 3000);
              }
            }
          }
        });
        
        if (!hasInteraction && this.isMessageParticle && !this.isFading) {
          this.isVisible = false;
        }
        
        // Handle fading
        if (this.isFading) {
          this.fadeOpacity -= 0.05;
          if (this.fadeOpacity <= 0) {
            this.isVisible = false;
          }
        }
        
        const springDx = this.originX - this.x;
        const springDy = this.originY - this.y;
        this.vx += springDx * this.springForce;
        this.vy += springDy * this.springForce;
        
        this.vx *= this.friction;
        this.vy *= this.friction;
        
        this.x += this.vx;
        this.y += this.vy;
        
        // Check if particle should be visible (popped) - only for non-message particles
        if (!this.isMessageParticle) {
          const distFromOrigin = Math.sqrt(
            Math.pow(this.x - this.originX, 2) + 
            Math.pow(this.y - this.originY, 2)
          );
          
          // Pop into visibility when displaced enough
          if (distFromOrigin > this.visibilityThreshold) {
            this.isVisible = true;
          } else if (distFromOrigin < 1) {
            // Hide when returned close to origin
            this.isVisible = false;
          }
        }
      }

      draw() {
        if (this.isVisible) {
          ctx.save();
          
          if (this.isMessageParticle) {
            // Message particles are brighter and more opaque when revealed
            ctx.globalAlpha = 0.2 * this.fadeOpacity;
            ctx.fillStyle = '#000000'; // Black for better contrast
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.radius * 1.5, 0, Math.PI * 2);
            ctx.fill();
          } else {
            // Regular particles
            ctx.globalAlpha = 0.5;
            ctx.fillStyle = this.color;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
            ctx.fill();
          }
          
          ctx.restore();
        }
      }
    }

    // Function to get random position avoiding edges and center
    const getRandomMessagePosition = () => {
      const margin = 0.2; // Keep 20% margin from edges
      const centerExclusionRadius = 0.30; // Radius of center area to avoid (25% of screen)
      
      let position;
      let isValid = false;
      
      // Keep trying until we find a valid position
      while (!isValid) {
        position = {
          x: margin + Math.random() * (1 - 2 * margin),
          y: margin + Math.random() * (1 - 2 * margin)
        };
        
        // Check if position is outside the center circular area
        const distFromCenter = Math.sqrt(
          Math.pow(position.x - 0.5, 2) + 
          Math.pow(position.y - 0.5, 2)
        );
        
        if (distFromCenter > centerExclusionRadius) {
          isValid = true;
        }
      }
      
      return position;
    };

    // Function to create message pattern
    const createMessagePattern = (message, posX = 0.5, posY = 0.5) => {
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      // Set canvas size based on parent container
      const rect = parentContainer.getBoundingClientRect();
      tempCanvas.width = rect.width;
      tempCanvas.height = rect.height;
      
      // Configure text
      const fontSize = Math.min(rect.width / 15, 40); // Responsive font size
      tempCtx.font = `bold ${fontSize}px Arial`;
      tempCtx.fillStyle = 'black';
      tempCtx.textAlign = 'center';
      tempCtx.textBaseline = 'middle';
      
      // Draw text at specified position
      const x = tempCanvas.width * posX;
      const y = tempCanvas.height * posY;
      tempCtx.fillText(message, x, y);
      
      // Get image data to find where text pixels are
      const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      const pixels = imageData.data;
      
      const messageCoords = [];
      const spacing = 4; // Sample every 4 pixels for message
      
      for (let y = 0; y < tempCanvas.height; y += spacing) {
        for (let x = 0; x < tempCanvas.width; x += spacing) {
          const index = (y * tempCanvas.width + x) * 4;
          // Check if pixel is not transparent (text is here)
          if (pixels[index + 3] > 128) {
            messageCoords.push({ x, y });
          }
        }
      }
      
      return messageCoords;
    };

    const createNewMessage = () => {
      // Clear old message particles
      messageParticlesRef.current.forEach(particle => {
        const index = particlesRef.current.indexOf(particle);
        if (index > -1) {
          particlesRef.current.splice(index, 1);
        }
      });
      messageParticlesRef.current.clear();
      
      // Get next message
      currentMessageIndexRef.current = (currentMessageIndexRef.current + 1) % messages.length;
      const newMessage = messages[currentMessageIndexRef.current];
      
      // Get random position
      messagePositionRef.current = getRandomMessagePosition();
      
      // Create new message pattern
      const messageCoords = createMessagePattern(
        newMessage, 
        messagePositionRef.current.x, 
        messagePositionRef.current.y
      );
      
      // Add new message particles
      messageCoords.forEach(coord => {
        const particle = new Particle(coord.x, coord.y, true);
        particlesRef.current.push(particle);
        messageParticlesRef.current.add(particle);
      });
      
      // Reset reveal state
      messageRevealedRef.current = false;
    };

    const initParticles = () => {
      particlesRef.current = [];
      messageParticlesRef.current.clear();
      
      const spacing = 10;
      const cols = Math.floor(canvas.width / spacing);
      const rows = Math.floor(canvas.height / spacing);
      
      // Create regular grid first
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * spacing + spacing / 2;
          const y = j * spacing + spacing / 2;
          particlesRef.current.push(new Particle(x, y, false));
        }
      }
      
      // Create initial message
      currentMessageIndexRef.current = 0;
      const messageCoords = createMessagePattern(
        messages[0], 
        messagePositionRef.current.x, 
        messagePositionRef.current.y
      );
      
      // Add message particles
      messageCoords.forEach(coord => {
        const particle = new Particle(coord.x, coord.y, true);
        particlesRef.current.push(particle);
        messageParticlesRef.current.add(particle);
      });
    };

    const updateCanvasSize = () => {
      const rect = parentContainer.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      initParticles();
    };
    
    updateCanvasSize();
    
    const resizeObserver = new ResizeObserver(updateCanvasSize);
    resizeObserver.observe(parentContainer);

    const deactivateMouse = () => {
      mouseRef.current = { x: -1000, y: -1000 };
      isMouseInBounds.current = false;
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const newX = e.clientX - rect.left;
      const newY = e.clientY - rect.top;
      
      // Check if mouse has actually moved
      if (newX !== lastMousePosRef.current.x || newY !== lastMousePosRef.current.y) {
        mouseRef.current = { x: newX, y: newY };
        lastMousePosRef.current = { x: newX, y: newY };
        isMouseInBounds.current = true;
        
        // Clear existing timeout
        if (mouseTimeoutRef.current) {
          clearTimeout(mouseTimeoutRef.current);
        }
        
        // Set new timeout
        mouseTimeoutRef.current = setTimeout(() => {
          deactivateMouse();
        }, mouseTimeoutSeconds * 1000);
      }
    };

    const handleMouseEnter = () => {
      // Don't automatically activate on enter - wait for movement
    };

    const handleMouseLeave = () => {
      if (mouseTimeoutRef.current) {
        clearTimeout(mouseTimeoutRef.current);
      }
      deactivateMouse();
    };

    const handleClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Create a new wave at the click position
      wavesRef.current.push(new Wave(x, y));
      
      // Limit the number of waves to prevent memory issues
      if (wavesRef.current.length > 5) {
        wavesRef.current = wavesRef.current.filter(wave => wave.active);
      }
    };

    // Touch event handlers
    const handleTouchStart = (e) => {
      const rect = canvas.getBoundingClientRect();
      
      // Store initial touch position and time for tap detection
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        touchStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          time: Date.now()
        };
        isTapRef.current = true;
      }
      
      // Process all touches
      for (let i = 0; i < e.touches.length; i++) {
        const touch = e.touches[i];
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        // Store touch position
        activeTouchesRef.current.set(touch.identifier, { x, y });
      }
    };

    const handleTouchMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      
      // Check if this is a significant move (not a tap)
      if (e.touches.length === 1 && isTapRef.current) {
        const touch = e.touches[0];
        const moveDistance = Math.sqrt(
          Math.pow(touch.clientX - touchStartRef.current.x, 2) +
          Math.pow(touch.clientY - touchStartRef.current.y, 2)
        );
        
        // If moved more than 10 pixels, it's not a tap
        if (moveDistance > 10) {
          isTapRef.current = false;
        }
      }
      
      // Update all active touches
      for (let i = 0; i < e.touches.length; i++) {
        const touch = e.touches[i];
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        activeTouchesRef.current.set(touch.identifier, { x, y });
      }
      
      // If there's at least one touch, update mouse position for compatibility
      if (e.touches.length > 0) {
        const firstTouch = e.touches[0];
        mouseRef.current = {
          x: firstTouch.clientX - rect.left,
          y: firstTouch.clientY - rect.top
        };
        isMouseInBounds.current = true;
        
        // Reset timeout
        if (mouseTimeoutRef.current) {
          clearTimeout(mouseTimeoutRef.current);
        }
        mouseTimeoutRef.current = setTimeout(() => {
          deactivateMouse();
          activeTouchesRef.current.clear();
        }, mouseTimeoutSeconds * 1000);
      }
    };

    const handleTouchEnd = (e) => {
      // Check if this was a tap (short duration, minimal movement)
      if (e.changedTouches.length === 1 && isTapRef.current) {
        const touchDuration = Date.now() - touchStartRef.current.time;
        if (touchDuration < 200) { // Less than 200ms is considered a tap
          const rect = canvas.getBoundingClientRect();
          const touch = e.changedTouches[0];
          const x = touch.clientX - rect.left;
          const y = touch.clientY - rect.top;
          
          // Create wave effect for tap
          wavesRef.current.push(new Wave(x, y));
          
          // Limit waves
          if (wavesRef.current.length > 5) {
            wavesRef.current = wavesRef.current.filter(wave => wave.active);
          }
        }
      }
      
      // Remove ended touches
      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        activeTouchesRef.current.delete(touch.identifier);
      }
      
      // If no more touches, deactivate after timeout
      if (e.touches.length === 0) {
        if (mouseTimeoutRef.current) {
          clearTimeout(mouseTimeoutRef.current);
        }
        mouseTimeoutRef.current = setTimeout(() => {
          deactivateMouse();
          activeTouchesRef.current.clear();
        }, mouseTimeoutSeconds * 1000);
      }
      
      isTapRef.current = false;
    };

    const handleTouchCancel = (e) => {
      handleTouchEnd(e);
    };

    // Add event listeners to the parent container
    parentContainer.addEventListener('mousemove', handleMouseMove);
    parentContainer.addEventListener('mouseenter', handleMouseEnter);
    parentContainer.addEventListener('mouseleave', handleMouseLeave);
    parentContainer.addEventListener('click', handleClick);
    
    // Add touch event listeners with passive: true to allow scrolling
    parentContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    parentContainer.addEventListener('touchmove', handleTouchMove, { passive: true });
    parentContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
    parentContainer.addEventListener('touchcancel', handleTouchCancel, { passive: true });
    
    // Make canvas respond to pointer events for clicks
    canvas.style.pointerEvents = 'auto';
    // Remove touchAction restriction to allow scrolling

    const animate = () => {
      ctx.fillStyle = '#ebe9dc';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Check if all message particles have faded
      const allFaded = Array.from(messageParticlesRef.current).every(
        particle => particle.fadeOpacity <= 0
      );
      
      if (allFaded && messageRevealedRef.current) {
        createNewMessage();
      }
      
      // Update and draw waves
      wavesRef.current = wavesRef.current.filter(wave => {
        wave.update();
        
        // Apply wave effect to particles
        particlesRef.current.forEach(particle => {
          wave.affectParticle(particle);
        });
        
        wave.draw();
        return wave.active;
      });
      
      // Update and draw particles
      particlesRef.current.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (mouseTimeoutRef.current) {
        clearTimeout(mouseTimeoutRef.current);
      }
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
      resizeObserver.disconnect();
      parentContainer.removeEventListener('mousemove', handleMouseMove);
      parentContainer.removeEventListener('mouseenter', handleMouseEnter);
      parentContainer.removeEventListener('mouseleave', handleMouseLeave);
      parentContainer.removeEventListener('click', handleClick);
      parentContainer.removeEventListener('touchstart', handleTouchStart);
      parentContainer.removeEventListener('touchmove', handleTouchMove);
      parentContainer.removeEventListener('touchend', handleTouchEnd);
      parentContainer.removeEventListener('touchcancel', handleTouchCancel);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [mouseTimeoutSeconds]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 ${className}`}
    />
  );
};

export default ParticleGrid;